import styled from 'styled-components';
import { color, fontSize, fontWeight, radius, screen, spacing } from 'theme';

const Root = styled.div`
  background: #d7edf1;
  padding-top: 4rem;

  h2 {
    margin-bottom: 1rem;
  }

  figure {
    margin: 0 auto 2rem auto;
    max-width: 85%;
  }

  .no-results {
    padding: 4rem 0 6rem 0;

    svg {
      color: #55c6be;
    }
  }

  legend > span > span {
    font-size: 0.875rem;

    @media (min-width: ${screen('xl')}) {
      font-size: 1rem;
    }
  }

  fieldset > div {
    padding: 0.25rem;

    @media (min-width: ${screen('xl')}) {
      padding: 0.75rem;
    }
  }

  table {
    margin: 0 auto;
    font-size: 0.65rem;
    cell-spacing: 0;
    border-collapse: collapse;
    width: 100%;

    @media (min-width: ${screen('xl')}) {
      font-size: 1rem;
    }

    th,
    td {
      padding: 0.25rem;
    }

    th {
      text-align: center;
      border-bottom: 1px solid #eee;
    }

    tr td:first-child {
      text-align: right;
    }

    td {
      padding: 0.15rem;

      @media (min-width: ${screen('xl')}) {
        padding: 0.25rem;
      }

      &.max > div {
        border-color: rgba(0, 0, 0, 0.4);
      }

      > div {
        border: 3px solid transparent;
        background: #f8f8f8;
        font-variant-numeric: tabular-nums;
        border-radius: 5px;
        padding: 0.5rem 0.75rem;
      }

      &.right {
        text-align: center;
      }
    }

    tfoot td {
      border-top: 1px solid #eee;
      padding-top: 0.5rem;
      font-size: 0.875rem;
      color: #333;
      font-style: italic;
    }
  }
`;

const Salaries = styled.div`
  background: #55c6be;
  border-radius: 15px;
  padding: 2rem 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 0 1rem 2rem 1rem;

  @media (min-width: ${screen('xl')}) {
    background: url('/images/home-v2/home-v2-hero-bg.svg') no-repeat top center;
    background-size: contain;
    border-radius: 0;
    padding: 7rem 4rem 0 4rem;
  }

  h2,
  h3 {
    color: #fff;
  }

  .fieldset-wrapper {
    margin: 0;

    @media (min-width: ${screen('xl')}) {
      margin: 0 4rem;
    }
  }

  .number {
    background: url('/images/contact/contact-form-bg.svg') no-repeat top center;
    background-size: contain;
    padding: 1.5rem 1.5rem;
    text-align: center;
    font-size: 2.5rem;
    line-height: 0;
    font-weight: 800;

    strong {
      font-size: 1rem;
      font-weight: 800;
      color: rgba(0, 0, 0, 0.6);
      text-transform: uppercase;
    }

    p {
      font-family: 'Big Shoulders Display', sans-serif;
    }
  }
`;

export const Chart = styled.div`
  overflow-y: hidden;
  overflow-x: hidden;

  @media (max-width: ${screen('mdMax')}) {
    overflow-x: auto;
  }

  .recharts-surface {
    max-height: 100%;
    margin-top: 3px;
  }

  .recharts-cartesian-axis-line {
    stroke: ${color('midnight_200')};
  }

  .recharts-text {
    fill: ${color('text_default')};
    font-size: ${fontSize('sm')};
    text-transform: uppercase;
    font-weight: ${fontWeight('medium')};
  }

  .recharts-label {
    text-transform: inherit;
    fill: ${color('text_subtle')};
  }

  .recharts-legend-wrapper {
    right: -${spacing('lg')} !important;
  }

  .recharts-legend-item {
    margin-bottom: ${spacing('xs')};
    display: flex !important;
    align-items: center;
  }

  .recharts-legend-item-text {
    color: ${color('text_default')} !important;
    font-size: ${fontSize('sm')};
    text-align: left;
  }

  .recharts-default-tooltip {
    background-color: ${color('midnight_800')} !important;
    border: 0 !important;
    border-radius: ${radius('sm')};
    padding: ${spacing('sm')} !important;
    font-size: ${fontSize('sm')};
    color: ${color('text_white')};
    font-weight: ${fontWeight('medium')};
    position: relative;

    &::before {
      content: '';
      width: 0;
      height: 0;
      position: absolute;
    }
  }

  .recharts-tooltip-wrapper-bottom .recharts-default-tooltip.tooltip-y::before {
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid ${color('midnight_800')};
    top: -5px;
    left: 50%;
    right: unset;
    transform: translateX(-50%);
  }

  .recharts-tooltip-wrapper-top .recharts-default-tooltip.tooltip-y::before {
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid ${color('midnight_800')};
    bottom: -5px;
    left: 50%;
    right: unset;
    transform: translateX(-50%);
  }

  .recharts-tooltip-wrapper-left .recharts-default-tooltip.tooltip-x::before {
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-left: 6px solid ${color('midnight_800')};
    right: -5px;
    left: unset;
    top: 50%;
    transform: translateY(-50%);
  }

  .recharts-tooltip-wrapper-right .recharts-default-tooltip.tooltip-x::before {
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-right: 6px solid ${color('midnight_800')};
    left: -5px;
    top: 50%;
    transform: translateY(-50%);
  }

  .recharts-tooltip-item {
    padding: 0 !important;
    color: inherit !important;
  }

  .recharts-tooltip-item-name {
    font-weight: ${fontWeight('extraBold')};
  }

  .recharts-label {
    fill: #fff;
  }
`;

export const YourOfferSalaryResultsStyles = {
  Root,
  Salaries,
  Chart
};
